<template>
	<div class="external-services-page p-6">
		<h1
			class="text-2xl font-bold mb-6 text-white flex items-center justify-between"
		>
			<div class="flex items-center">
				<i class="mdi mdi-web mr-3 text-teal-400"></i>
				Gestione Web Services
			</div>

			<div class="flex items-center gap-4">
				<label
					class="flex items-center cursor-pointer text-sm font-medium text-gray-300 hover:text-white transition-colors"
				>
					<input
						type="checkbox"
						v-model="showArchived"
						class="mr-2 w-4 h-4 rounded border-gray-600 bg-gray-700 text-teal-500 focus:ring-teal-500 focus:ring-offset-gray-800"
					/>
					Mostra Archiviati
				</label>

				<button
					@click="testPublicApi"
					class="flex items-center gap-2 px-3 py-1.5 bg-teal-500/10 hover:bg-teal-500/20 text-teal-400 rounded text-sm font-medium transition-colors border border-teal-500/20"
				>
					<i class="mdi mdi-open-in-new"></i>
					Test Public API
				</button>
			</div>
		</h1>

		<div
			class="bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-gray-700"
		>
			<div class="overflow-x-auto">
				<table class="w-full text-left border-collapse">
					<thead>
						<tr
							class="bg-gray-900 text-gray-400 text-sm uppercase tracking-wider"
						>
							<th class="p-4 font-semibold border-b border-gray-700">
								Device ID
							</th>
							<th class="p-4 font-semibold border-b border-gray-700">
								Device Name
							</th>
							<th class="p-4 font-semibold border-b border-gray-700">Status</th>
							<th class="p-4 font-semibold border-b border-gray-700 text-right">
								Public API
							</th>
						</tr>
					</thead>
					<tbody class="divide-y divide-gray-700">
						<tr
							v-for="device in displayedDevices"
							:key="device.id"
							class="hover:bg-gray-750 transition-colors"
						>
							<td class="p-4 text-gray-300 font-mono">{{ device.id }}</td>
							<td class="p-4 text-white font-medium">
								{{ device.name || "Unnamed Device" }}
							</td>
							<td class="p-4">
								<span
									:class="[
										'px-3 py-1 rounded-full text-xs font-bold bg-opacity-20 border',
										device.status === 'online'
											? 'bg-green-500 text-green-400 border-green-500'
											: 'bg-red-500 text-red-400 border-red-500',
									]"
								>
									<i
										:class="[
											'mdi mr-1',
											device.status === 'online'
												? 'mdi-circle-slice-8'
												: 'mdi-alert-circle',
										]"
									></i>
									{{ device.status.toUpperCase() }}
								</span>
							</td>
							<td class="p-4 text-right">
								<div class="flex items-center justify-end">
									<label
										class="relative inline-flex items-center cursor-pointer"
									>
										<input
											type="checkbox"
											v-model="device.is_public"
											class="sr-only peer"
											@change="togglePublic(device)"
										/>
										<div
											class="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-500 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-teal-500"
										></div>
										<span class="ml-3 text-sm font-medium text-gray-300 w-8">
											{{ device.is_public ? "ON" : "OFF" }}
										</span>
									</label>
								</div>
							</td>
						</tr>
						<tr v-if="displayedDevices.length === 0 && !loading">
							<td colspan="4" class="p-8 text-center text-gray-500">
								Nessun dispositivo trovato.
							</td>
						</tr>
						<tr v-if="loading">
							<td colspan="4" class="p-8 text-center text-gray-400">
								<i class="mdi mdi-loading mdi-spin mr-2"></i> Caricamento...
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import axios from "@/axiosInstance";

interface Device {
	id: number;
	name: string;
	status: string;
	is_public: boolean;
	is_archived: boolean;
	user_id: number;
	user?: {
		is_admin?: boolean;
	};
}

export default defineComponent({
	name: "ExternalServices",
	setup() {
		const devices = ref<Device[]>([]);
		const loading = ref(true);
		const showArchived = ref(false);
		const fetchDevices = async () => {
			try {
				loading.value = true;
				// Uses configured axios instance
				// Correct endpoint found in DevicesList.vue: /devices/get-all-graphs
				const res = await axios.get("/devices/get-all-graphs?all=true");
				devices.value = res.data.map((d: any) => ({
					...d,
					is_public: !!d.is_public,
				}));
			} catch (err) {
				console.error("Error fetching devices:", err);
			} finally {
				loading.value = false;
			}
		};

		const displayedDevices = computed(() => {
			if (showArchived.value) {
				return devices.value;
			}
			return devices.value.filter((d) => !d.is_archived);
		});

		const testPublicApi = async () => {
			try {
				const res = await axios.get("/devices/public");
				const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
				const url = URL.createObjectURL(blob);
				window.open(url, "_blank");
			} catch (err) {
				console.error("Error calling public API:", err);
				alert("Errore durante il test della Public API.");
			}
		};

		const togglePublic = async (device: Device) => {
			try {
				await axios.post(`/devices/${device.id}/thresholds`, {
					is_public: device.is_public,
				});
			} catch (err) {
				console.error("Error updating privacy:", err);
				// Revert on error
				device.is_public = !device.is_public;
				alert("Errore nell'aggiornamento dello stato pubblico.");
			}
		};

		onMounted(() => {
			fetchDevices();
		});

		return {
			devices,
			displayedDevices,
			loading,
			showArchived,
			testPublicApi,
			togglePublic,
		};
	},
});
</script>

<style scoped>
/* Custom toggle styles if needed, but Tailwind classes cover most */
</style>
